import html2pdf from 'html2pdf.js';
import { api } from './api';
import { clientQtyLine, splitSheets } from './jobCardQty';
import { sheetDimsForCard } from './units';

interface PrintRow {
  Job_ID: string;
  PO_RefNumber: string | null;
  Job_Name: string | null;
  JobOrder_date: string | null;
  Total_Boxes: string | null;
  TotalSheet_withWastage: string | null;
  Ups: string | null;
  Die_Cutting: string | null;
  Selected_Die: string | null;
  Sheet_Size: string | null;
  Sheet_CutSize_length: string | null;
  Sheet_CutSize_Width: string | null;
  Paper_Type: string | null;
  Lamination_Type: string | null;
  Machine: string | null;
  Remarks: string | null;
  PO_Number: string | null;
  PO_Date: string | null;
  PO_deliverydate: string | null;
  PO_Quantity: string | null;
  PO_Artwork: string | null;
  PO_OrderType: string | null;
  Job_OrderType: string | null;
  Client_Name: string | null;
  Product_Name: string | null;
  Product_Length: string | null;
  Product_Width: string | null;
  Product_Height: string | null;
  Product_Artwork: string | null;
  Product_AW_Material_No: string | null;
  FoilingColor: string | null;
  FilmSize_Micron: string | null;
  EmbossingNo: string | null;
  ShadeCardNo: string | null;
  PrePostLamination: string | null;
  OnlineEmbossing: boolean | null;
  OfflineEmbossing: boolean | null;
  Braille: boolean | null;
  SpotUV: boolean | null;
  ScreenUV: boolean | null;
  OfflineCoating: boolean | null;
  OnlineCoating: boolean | null;
  CartonType: string | null;
  MetallicCarton: boolean | null;
  ThreePlyCarton: boolean | null;
  MachineFront: string | null;
  MachineFront_Color: string | null;
  MachineFront2: string | null;
  MachineFront2_Color: string | null;
  MachineFront2_Name?: string | null;
  MachineBack: string | null;
  MachineBack_Color: string | null;
  MachineFront_Name: string | null;
  MachineBack_Name: string | null;
  PaperType_Name: string | null;
  Sheet_GSM_From_Sheet: string | null;
  Product_CutSize_L: string | null;
  Product_CutSize_W: string | null;
  Product_Remark: string | null;
  PlateNumber: string | null;
  Lamination_Type_Name: string | null;
  Remark_Party_GSM: string | null;
  BP_No: string | null;
  Resolved_Sheet_Size: string | null;
  CutToCutSize: string | null;
  Client_Qty?: string | null;
  // PO qty the card recorded when this version was saved (null on older
  // cards), and when this version was issued if it has been re-saved.
  JobPOQty?: number | string | null;
  Card_Issued_On?: string | null;
}

function dt(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function yn(b: boolean | null): string { return b ? 'Yes' : 'No'; }
function esc(v: string | null | undefined): string {
  if (!v) return '';
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function buildCardHtml(r: PrintRow): string {
  // The PO qty this version was issued against; the live PO for older cards.
  const poQty = r.JobPOQty != null && r.JobPOQty !== '' ? String(r.JobPOQty) : (r.PO_Quantity ?? '');
  const sheets = splitSheets(r.Client_Qty, r.Ups, r.TotalSheet_withWastage, r.Total_Boxes, poQty);
  const crq = clientQtyLine(r.Client_Qty, poQty);
  const sheetSizeRaw = r.Resolved_Sheet_Size ?? r.Sheet_Size ?? '';
  const sheetGsm = r.Sheet_GSM_From_Sheet
    || (/\((\d+(?:\.\d+)?)\)\s*$/.exec(sheetSizeRaw)?.[1] ?? '')
    || r.Remark_Party_GSM
    || '';
  const orderType = (r.Job_OrderType ?? r.PO_OrderType ?? '').trim();
  // The mm figures print whole; the inches are worked out from the exact mm
  // (488.95 mm is 19.25 in — from the shortened 488 it came out as 19.21).
  const sheetSizeExact = sheetSizeRaw.replace(/\s*\(\d+(?:\.\d+)?\)\s*$/, '');
  const sheetCutExact = [r.Sheet_CutSize_length, r.Sheet_CutSize_Width].filter(Boolean).join(' x ');
  // Whole mm with the inches beside; a legacy card's inches as they were entered.
  const { display: sheetSizeDisplay, suffix: sheetSizeIn } = sheetDimsForCard(sheetSizeExact);
  const { display: sheetCutDisplay, suffix: sheetCutIn } = sheetDimsForCard(sheetCutExact);

  // No cell borders — only the section heading rules separate the card.
  const thS = 'padding:2px 10px 2px 0;font-weight:800;font-size:11px;vertical-align:top;color:#000;';
  const tdS = 'padding:2px 10px 2px 0;font-weight:500;font-size:11.5px;vertical-align:top;color:#1a1a1a;word-break:break-all;overflow-wrap:break-word;';
  const tblS = 'width:100%;table-layout:fixed;border-collapse:collapse;margin-bottom:5px;';
  const colG = '<colgroup><col style="width:17%"><col style="width:33%"><col style="width:17%"><col style="width:33%"></colgroup>';
  const shS = (t: string) => `<div style="font-size:12px;font-weight:900;text-transform:uppercase;letter-spacing:0.05em;padding:2px 0;margin-top:6px;border-bottom:2px solid #000;color:#000;">${t}</div>`;

  const th = (t: string) => `<td style="${thS}">${t}</td>`;
  const td = (t: string, span = 1) => `<td style="${tdS}"${span > 1 ? ` colspan="${span}"` : ''}>${t}</td>`;
  const row = (cells: string[]) => `<tr>${cells.join('')}</tr>`;

  const remarksHtml = (r.Product_Remark || r.Remarks) ? `
    ${shS('Remarks')}
    <div style="padding:4px 0;margin-bottom:5px;white-space:pre-wrap;font-size:10px;font-weight:500;color:#1a1a1a;">
      ${r.Product_Remark ? `<div style="margin-bottom:${r.Remarks ? '4px' : '0'}"><span style="font-weight:800;text-transform:uppercase;font-size:9px;color:#000;">Product: </span>${esc(r.Product_Remark)}</div>` : ''}
      ${r.Remarks ? `<div><span style="font-weight:800;text-transform:uppercase;font-size:9px;color:#000;">Job: </span>${esc(r.Remarks)}</div>` : ''}
    </div>` : '';

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Arial,Helvetica,sans-serif;color:#000;background:#fff;}
  </style></head><body>
    <div style="width:210mm;padding:12mm 15mm;box-sizing:border-box;line-height:1.4;">
      <div style="text-align:right;font-size:8px;font-weight:800;margin-bottom:2px;">PD/JC/01</div>

      <div style="border:2px solid #000;padding:6px 10px;margin-bottom:6px;text-align:center;">
        <div style="font-size:16px;font-weight:900;letter-spacing:0.05em;">VASUDEV PRINT PACK PVT. LTD.</div>
        <div style="font-size:8.5px;font-weight:600;">Hansa Industrial Park, Derabassi-Barwala Rd, Dera Bassi, Punjab 140507 · Phone: 085807 03745 · mis@vasudevprintpack.com</div>
        <div style="margin-top:2px;font-size:12px;font-weight:900;text-transform:uppercase;letter-spacing:0.15em;">Job Card</div>
      </div>

      <table style="${tblS}">${colG}<tbody>
        ${row([th('Job ID'), td(`${esc(r.Job_ID)}${orderType ? ` (${orderType.toUpperCase()})` : ''}`), th('PO Date'), td(dt(r.PO_Date))])}
        ${row([th('Job Card Date'), td(dt(r.Card_Issued_On ?? r.JobOrder_date)), th('PO Qty'), td(esc(poQty) || '—')])}
        ${row([th(''), td(''), th('K to K Size'), td(esc(r.CutToCutSize ?? ''))])}
        ${row([th('Product Name'), td(esc(r.Product_Name ?? r.Job_Name), 3)])}
        ${row([th('Client'), td(esc(r.Client_Name), 3)])}
        ${crq != null ? row([th(''), td(''), th(crq.label), td(esc(crq.value))]) : ''}
        ${row([th('PO No.'), td(esc(r.PO_Number)), th('Total Print Box'), td(esc(r.Total_Boxes))])}
      </tbody></table>

      ${shS('Cutting')}
      <table style="${tblS}">${colG}<tbody>
        ${row([th('Board (Paper Type)'), td(esc(r.PaperType_Name ?? r.Paper_Type)), th('GSM'), td(esc(sheetGsm))])}
        ${row([th('Full Sheet Size'), td(`${esc(sheetSizeDisplay)}${sheetSizeIn}`), th('Final Cut Size'), td(`${esc(sheetCutDisplay)}${sheetCutIn}`)])}
        ${row([th('Actual Sheet'), td(sheets.base), th('Sheet (incl. W)'), td(esc(r.TotalSheet_withWastage))])}
        ${row([th('Wastage'), td(sheets.wastage), th('Party GSM'), td(esc(r.Remark_Party_GSM))])}
      </tbody></table>

      ${shS('Printing')}
      <table style="${tblS}">${colG}<tbody>
        ${row([th('Design No.'), td(esc(r.Product_Artwork ?? r.PO_Artwork)), th('No. of Plates'), td(esc(r.PlateNumber))])}
        ${row([th('Shade Card No.'), td(esc(r.ShadeCardNo)), th('Art / Mat No.'), td(esc(r.Product_AW_Material_No))])}
        ${row([th('Machine Front'), td(esc(r.MachineFront_Name ?? r.MachineFront)), th('Color'), td(esc(r.MachineFront_Color))])}
        ${row([th('Machine Front 2'), td(esc(r.MachineFront2_Name ?? r.MachineFront2)), th('Color'), td(esc(r.MachineFront2_Color))])}
        ${row([th('Machine Back'), td(esc(r.MachineBack_Name ?? r.MachineBack)), th('Color'), td(esc(r.MachineBack_Color))])}
        ${row([th('BP No.'), td(esc(r.BP_No)), th('Metallic Carton'), td(yn(r.MetallicCarton))])}
      </tbody></table>

      ${shS('Top-Finish (Coating &amp; Lamination)')}
      <table style="${tblS}">${colG}<tbody>
        ${row([th('Top Finish'), td(esc(r.Lamination_Type_Name ?? r.Lamination_Type)), th('Pre/Post Lam.'), td(esc(r.PrePostLamination))])}
        ${row([th('Foiling Color'), td(esc(r.FoilingColor)), th('Film Size'), td(esc(r.FilmSize_Micron))])}
        ${row([th('Spot UV'), td(yn(r.SpotUV)), th('Screen UV'), td(yn(r.ScreenUV))])}
        ${row([th('Online Coating'), td(yn(r.OnlineCoating)), th('Offline Coating'), td(yn(r.OfflineCoating))])}
      </tbody></table>

      ${shS('Die &amp; Carton Structure')}
      <table style="${tblS}">${colG}<tbody>
        ${row([th('Die No.'), td(esc(r.Die_Cutting)), th('Ups'), td(esc(r.Ups))])}
        ${row([th('Carton Type'), td(esc(r.CartonType)), th('3-Ply Carton'), td(yn(r.ThreePlyCarton))])}
        ${row([th('Product L×W×H'), td([r.Product_Length, r.Product_Width, r.Product_Height].filter(Boolean).join(' × ')), th('Braille'), td(yn(r.Braille))])}
        ${row([th('Online Embossing'), td(yn(r.OnlineEmbossing)), th('Offline Embossing'), td(yn(r.OfflineEmbossing))])}
        ${row([th('Embossing No.'), td(esc(r.EmbossingNo)), th(''), td('')])}
      </tbody></table>

      ${remarksHtml}

      <div style="display:flex;justify-content:space-between;gap:40px;margin-top:30px;margin-bottom:6px;">
        <div style="flex:1;text-align:center;border-top:1px solid #000;padding-top:4px;font-size:10px;font-weight:700;">Created By (Signature)</div>
        <div style="flex:1;text-align:center;border-top:1px solid #000;padding-top:4px;font-size:10px;font-weight:700;">Checked By (Signature)</div>
      </div>
      <div style="font-size:8px;font-weight:600;text-align:right;margin-top:8px;color:#333;">
        All dimensions in mm (inch equivalents in brackets). Generated by Ab CRM v2 on ${new Date().toLocaleString()}.
      </div>
    </div>
  </body></html>`;
}

export async function generateJobCardPdf(jobId: string): Promise<void> {
  const r = await api<PrintRow>(`/api/job-orders/${encodeURIComponent(jobId)}/print`);
  const html = buildCardHtml(r);

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:210mm;height:297mm;visibility:hidden;';
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument!;
    doc.open();
    doc.write(html);
    doc.close();

    const filename = `Job-Card-${jobId.replace(/\//g, '-')}.pdf`;
    await html2pdf().set({
      margin: 0,
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    } as any).from(doc.body).save();
  } finally {
    document.body.removeChild(iframe);
  }
}
